import React, {
  createContext, useCallback, useContext, useEffect, useMemo, useRef, useState,
} from 'react';
import { datadogRum } from '@datadog/browser-rum';
import { useSnackBar } from '@usealloy/component-library';
import { AREA_COOKIE_NAMES } from '@usealloy/daffy-to-frontend-dashboard-rollout';

import { ajaxFetch } from '~/helpers/api';
import { routes } from '~/helpers/routes';
import { UserContext } from '~/dashboard/context';

import {
  BETA_COOKIE_OPTIONS,
  BETA_TOGGLE_DD_ACTION,
  BETA_WRITE_ERROR_COPY,
  COUPLED_ON,
} from './constants';
import type { BetaKey, BetaSource } from './constants';
import { applyNavAction, resolveNavAction } from './navigation';

export type BetaPreferencesContextValue = {
  /** The surface this provider serves (telemetry + nav strategy). */
  source: BetaSource;
  /** Beta keys available to this org (gated by LaunchDarkly). */
  keys: BetaKey[];
  /** Whether any beta entry point should show at all. */
  isAvailable: boolean;
  /** True while any beta write is in flight — the global lock + Settings "Saving…" cue. */
  isPending: boolean;
  /** The key currently being written, or null (drives the per-row spinner in menus). */
  loadingKey: BetaKey | null;
  /** Effective on/off state for a key (derived from cookies, never optimistic). */
  isChecked: (key: BetaKey) => boolean;
  /** True when the key is locked on read-only via coupling (betaRoutes ⇒ fepSidenav). */
  isReadOnly: (key: BetaKey) => boolean;
  /** Persist a beta preference and run the full write lifecycle. */
  setEnabled: (key: BetaKey, enabled: boolean) => Promise<void>;
};

const noopAsync = async () => {};

export const BetaPreferencesContext = createContext<BetaPreferencesContextValue>({
  source: 'settings',
  keys: [],
  isAvailable: false,
  isPending: false,
  loadingKey: null,
  isChecked: () => false,
  isReadOnly: () => false,
  setEnabled: noopAsync,
});

const postBetaCookie = (cookie: BetaKey, enabled: boolean) => ajaxFetch(routes.beta.cookie, {
  method: 'POST',
  headers: { 'Content-type': 'application/json' },
  body: JSON.stringify({ cookie, enabled }),
});

type Props = React.PropsWithChildren<{ source: BetaSource }>;

/**
 * Headless controller for the beta-preference toggles, shared by Settings, the agent menu,
 * and a future banner (adapters only render state and call `setEnabled`). Owns the whole
 * write lifecycle: cookie write(s) incl. coupling, telemetry, post-success nav/reload, and
 * the failure snackbar. Mount one per surface subtree with the appropriate `source`.
 */
export function BetaPreferencesProvider({ source, children }: Props) {
  const { betaCookies, featureFlags, getUserContext } = useContext(UserContext);
  const [loadingKey, setLoadingKey] = useState<BetaKey | null>(null);
  const addSnackBar = useSnackBar();

  const keys = useMemo(
    () => BETA_COOKIE_OPTIONS
      .filter(({ ldFlag }) => !!featureFlags?.[ldFlag])
      .map(({ key }) => key),
    [featureFlags],
  );

  // The sidenav is bundled into every beta area experience: while any area is both
  // flagged in and active, fepSidenav is forced on and locked read-only. Only fepSidenav
  // itself can be locked, so bail before scanning the area set for any other key.
  const isReadOnly = useCallback(
    (key: BetaKey) => {
      if (key !== 'fepSidenav') return false;
      return BETA_COOKIE_OPTIONS
        .some(({ key: optionKey, ldFlag }) => (
          AREA_COOKIE_NAMES.includes(optionKey)
          && !!featureFlags?.[ldFlag]
          && !!betaCookies?.[optionKey]
        ));
    },
    [featureFlags, betaCookies],
  );

  // FRONTEND-632: `checked` must stay honest — it always reflects the real betaCookies value,
  // and is never forced true by the read-only lock. Only interactivity is locked (isReadOnly);
  // the backend self-heals fepSidenav to true whenever betaRoutes is active, so the box can't
  // show checked+locked while the real cookie (and thus the SideNav) is actually off.
  const isChecked = useCallback(
    (key: BetaKey) => !!betaCookies?.[key],
    [betaCookies],
  );

  const setEnabled = useCallback(
    async (key: BetaKey, enabled: boolean) => {
      datadogRum.addAction(BETA_TOGGLE_DD_ACTION, { key, enabled, source });
      setLoadingKey(key);
      try {
        await postBetaCookie(key, enabled);
        const coupled = COUPLED_ON[key];
        if (enabled && coupled && !!featureFlags?.[coupled] && !betaCookies?.[coupled]) {
          await postBetaCookie(coupled, true);
        }
        // Success: hand off to the surface's nav strategy. A reload / hard-nav re-derives
        // all state from the freshly written cookies, so there's no optimistic flip.
        applyNavAction(resolveNavAction({
          source, key, enabled, pathname: window.location.pathname,
        }));
      } catch {
        // Stay put, surface the error, and refresh so the UI reflects whatever combination
        // of cookies actually persisted (e.g. betaRoutes succeeded but fepSidenav didn't).
        addSnackBar({ title: BETA_WRITE_ERROR_COPY, type: 'danger' });
        await getUserContext();
      } finally {
        setLoadingKey(null);
      }
    },
    [source, featureFlags, betaCookies, getUserContext, addSnackBar],
  );

  // FRONTEND-653: beta-surface-scoped refetch-on-focus. While a beta surface is mounted,
  // refresh user context when the tab is refocused so a change made in another tab shows
  // up — skipped while a write is in flight, and no global listener (this only exists for
  // as long as a beta surface is on screen).
  const isPendingRef = useRef(false);
  isPendingRef.current = loadingKey !== null;
  useEffect(() => {
    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible' && !isPendingRef.current) {
        getUserContext();
      }
    };
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => document.removeEventListener('visibilitychange', onVisibilityChange);
  }, [getUserContext]);

  const value = useMemo<BetaPreferencesContextValue>(
    () => ({
      source,
      keys,
      isAvailable: keys.length > 0,
      isPending: loadingKey !== null,
      loadingKey,
      isChecked,
      isReadOnly,
      setEnabled,
    }),
    [source, keys, loadingKey, isChecked, isReadOnly, setEnabled],
  );

  return (
    <BetaPreferencesContext.Provider value={value}>
      {children}
    </BetaPreferencesContext.Provider>
  );
}
